import { v4 as uuidv4 } from 'uuid';
import {
  ErrorTranslations,
  FormModel,
  FormModelExtenxions,
} from '../models/forms.model';
import { LabelInfo, TranslationInfo } from '../models/label-info.model';
import { Md5 } from 'ts-md5';
import { TranslationDto } from '../models/dto/dictionaryDto.model';
import { Language } from '../models/language.model';
export class Utils {
  static bigInt: number = Number(BigInt('900719925'));

  static json2csv(items: any[]): string {
    const replacer = (_key: any, value: null) => (value === null ? '' : value);
    let header = Object.keys(items[0]);
    header = header.filter((h) => h !== 'locked' && h != 'active');
    const csv = [
      header.join(','),
      ...items.map((row: { [x: string]: any }) =>
        header
          .map((fieldName) => JSON.stringify(row[fieldName], replacer))
          .join(','),
      ),
    ].join('\r\n');
    return csv;
  }

  static csv2json(csv: string) {
    const lines = csv.split(/\r?\n/).filter((line) => line.trim() !== '');
    if (lines.length === 0) return [];

    const headers = Utils.parseCsvLine(lines[0]);
    const result = [];

    for (let i = 1; i < lines.length; i++) {
      const obj: any = {};
      const currentline = Utils.parseCsvLine(lines[i]);
      for (let j = 0; j < headers.length; j++) {
        const lineText = currentline[j];
        if (lineText !== undefined) {
          obj[headers[j]] = Utils.trim(lineText, '"');
        }
      }
      result.push(obj);
    }

    return result;
  }

  static parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' && (i === 0 || line[i - 1] !== '\\')) {
        inQuotes = !inQuotes;
        continue;
      }
      if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  static trim(str: string, ch: string) {
    var start = 0,
      end = str.length;
    while (start < end && str[start] === ch) ++start;
    while (end > start && str[end - 1] === ch) --end;
    return start > 0 || end < str.length ? str.substring(start, end) : str;
  }

  static download(blob: Blob, name: string): void {
    const anchor = document.createElement('a');
    anchor.download = name;
    anchor.href = (window.webkitURL || window.URL).createObjectURL(blob);
    anchor.click();
  }

  static generateUUID(): string {
    return uuidv4();
  }

  static copyProperties(sourceObj: any, destObj: any) {
    Object.keys(sourceObj).forEach((key) => (destObj[key] = sourceObj[key]));
  }

  static correctDate(date: Date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  }

  static formatString(str: string, val: string[]) {
    for (let index = 0; index < val.length; index++) {
      str = str.replace(`{${index}}`, val[index]);
    }
    return str;
  }

  static removeNewLinesAndSpaces(str: string) {
    return str.replace(/(\r\n|\n|\r)/gm, '').replace(/\s/g, '');
  }

  static removeNewLines(str: string) {
    return str.replace(/(\r\n|\n|\r)/gm, '');
  }

  static findTranslation(
    translations: LabelInfo[],
    langIso: string,
  ): string | undefined {
    if (translations) {
      var tr = translations.find((t) => t.languageIso == langIso);
      if (tr && tr.value) {
        return tr.value;
      }
    }
    return '';
  }

  static findTranslationInfo(
    translationName: string,
    translations: TranslationInfo[],
    langIso: string,
  ): string | undefined {
    if (translations) {
      var tr = translations.find((t) => t.name == translationName);
      if (tr) {
        return this.findTranslation(tr.values, langIso);
      }
    }
    return '';
  }

  static findLanguageIsoById(
    languages: Language[],
    languageId: number,
  ): string {
    const match = languages.find((a) => a.id == languageId);
    if (match) {
      return match.iso;
    }
    return null as any;
  }

  static findLanguageIdByIso(languages: Language[], iso: string): number {
    const match = languages.find((a) => a.iso == iso);
    if (match) {
      return match.id;
    }
    return 0;
  }

  static findValueTranslation(
    translations: TranslationDto[],
    languageId: number,
  ): string | undefined {
    if (translations) {
      var tr = translations.find((t) => t.languageId == languageId);
      if (tr) {
        return tr.value;
      }
    }
    return undefined;
  }

  static findErrorTranslationValueOrDefault(
    errorsTranslations: ErrorTranslations[],
    langIso: string,
    errorCode: string,
  ): string {
    if (errorsTranslations) {
      const found = errorsTranslations.find((e) => e.code === errorCode);
      if (found) {
        return (
          found.translations.find((t) => t.languageIso === langIso)?.value ??
          'N/A'
        );
      }
    }
    return 'N/A';
  }

  static findErrorTranslationLabel(
    errorsTranslations: ErrorTranslations[],
    langIso: string,
    errorCode: string,
  ): LabelInfo | undefined {
    if (errorsTranslations) {
      const found = errorsTranslations.find((e) => e.code === errorCode);
      if (found) {
        return found.translations.find((t) => t.languageIso === langIso);
      }
    }
    return undefined;
  }

  static createErrorTranslationMssageWithParameters(
    errorsTranslations: ErrorTranslations[],
    langIso: string,
    errorCode: string,
    params: string[],
  ) {
    const translation = Utils.findErrorTranslationLabel(
      errorsTranslations,
      langIso,
      errorCode,
    );
    let errorMessage = 'An error has occured';
    if (translation && translation.value) {
      errorMessage = translation.value;
      if (params) {
        for (let i = 0; i < params.length; i++) {
          errorMessage = errorMessage.replace('{' + i + '}', params[i]);
        }
      }
    } else errorMessage = '';

    return errorMessage;
  }

  static objectsAreEqual(obj1: any, obj2: any) {
    if (obj1 && obj2) {
      const string = JSON.stringify(
        this.removeUndefinedAndSortProperties(obj1),
      );
      const string2 = JSON.stringify(
        this.removeUndefinedAndSortProperties(obj2),
      );
      let hash = Md5.hashStr(string);
      let hash2 = Md5.hashStr(string2);
      return hash === hash2;
    }
    if (obj1 && !obj2) {
      return false;
    }
    if (!obj1 && obj2) {
      return false;
    }
    return true;
  }

  static removeUndefinedAndSortProperties(obj: any) {
    if (typeof obj != 'object' || obj === null || obj === undefined) {
      return obj;
    }
    if (obj instanceof Array) {
      for (var i = 0; i < obj.length; i++) {
        obj[i] = Utils.removeUndefinedAndSortProperties(obj[i]);
      }
      return obj;
    }

    var keys = Object.keys(obj);
    keys.sort();
    let newObject: any = {};
    for (var i = 0; i < keys.length; i++) {
      var val = obj[keys[i]];
      if (val !== undefined) {
        newObject[keys[i]] = Utils.removeUndefinedAndSortProperties(val);
      }
    }
    return newObject;
  }

  static getFileExtension(fileName: string): string | undefined {
    if (fileName) {
      const splitted = fileName.split('.');
      if (splitted.length > 1) {
        return splitted.pop();
      }
    }
    return undefined;
  }

  static getFieldsFromState(
    state: FormModel,
    selectedMethodId: string,
    returnOwnFields?: boolean,
  ) {
    if (returnOwnFields) {
      let apiMethod = [
        ...(state.api?.getItems ?? []),
        ...(state.api?.sendItems ?? []),
        ...(state.api?.validationItems ?? []),
      ].find((m) => m.id === selectedMethodId);

      return apiMethod?.fields ?? [];
    } else {
      if (!state) {
        return [];
      }
      let ext = new FormModelExtenxions(state);
      let fluxFields = ext.fluxItemsWithGetApiMethods(selectedMethodId);

      return fluxFields;
    }
  }

  static base64toBlob(b64Data: string, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  static copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    } else {
      // Fallback for unsupported browsers
      return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
          document.body.removeChild(textarea);
          resolve();
        } catch (err) {
          document.body.removeChild(textarea);
          reject(err);
        }
      });
    }
  }
}
